// SCR-CONSOLE-004 — کاتالوگ پلن (US-PLT-020): فهرست پلن‌ها و نسخه‌ها، پلن تازه، نسخه پیش‌نویس (PlanVersionDialog) و انتشار با
// تأیید (مستأجرهای فعلی روی نسخه خود می‌مانند — AC-PLT-020.1).
import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap, type PlanVersionView, type PlanView } from '@phoenix/api-client';
import { useIdempotencyKey, usePlatformSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  ConfirmDialog,
  DataTable,
  faDigits,
  FormDialog,
  JalaliDate,
  ListPage,
  Money,
  QueryState,
  StatusChip,
  TextFieldRHF,
  type DataTableColumn,
  type StatusTone,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { consoleKeys, toNumber } from '../../api';
import { PlanVersionDialog } from './PlanVersionDialog';

export const SCR_CONSOLE_004 = 'SCR-CONSOLE-004';

function versionTone(status: string): StatusTone {
  return status === 'Published' ? 'active' : status === 'Draft' ? 'pending' : 'inactive';
}

const planSchema = z.object({
  code: z.string().regex(/^[a-z][a-z0-9_]{1,31}$/, { error: 'errors:error.plan_code_invalid' }),
  sortOrder: z.string().regex(/^\d{1,4}$/, { error: 'console:validation.non_negative_integer' }),
});
type PlanForm = z.input<typeof planSchema>;

function CreatePlanDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { api } = usePlatformSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState, setError, reset } = useForm<PlanForm>({
    resolver: zodResolver(planSchema),
    defaultValues: { code: '', sortOrder: '10' },
  });
  const create = useMutation({
    mutationFn: (values: PlanForm) =>
      unwrap(
        api.POST('/api/v1/platform/console/plans', {
          body: { code: values.code, sortOrder: Number(values.sortOrder) },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: consoleKeys.plans });
      reset();
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['code', 'sortOrder'],
        codeFields: { 'PHX-PLT-0201': 'code' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });
  return (
    <FormDialog
      open={open}
      titleKey="console:plans.create_title"
      onSubmit={() => void handleSubmit((values) => create.mutate(values))()}
      onClose={() => {
        reset();
        onClose();
      }}
      isDirty={formState.isDirty}
      isSubmitting={create.isPending}
      submitError={submitError}
    >
      <TextFieldRHF
        control={control}
        name="code"
        labelKey="console:plans.code"
        helperKey="console:plans.code_help"
        required
        fullWidth
        slotProps={{ htmlInput: { dir: 'ltr' } }}
      />
      <TextFieldRHF
        control={control}
        name="sortOrder"
        labelKey="console:plans.sort_order"
        required
        fullWidth
        slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric' } }}
      />
    </FormDialog>
  );
}

function PlanCard({
  plan,
  onNewVersion,
  onPublish,
}: {
  plan: PlanView;
  onNewVersion: () => void;
  onPublish: (v: PlanVersionView) => void;
}) {
  const { t } = useTranslation('console');
  const versions = [...plan.versions].sort((a, b) => (toNumber(b.version) ?? 0) - (toNumber(a.version) ?? 0));
  const columns: DataTableColumn<PlanVersionView>[] = [
    {
      id: 'version',
      headerKey: 'console:plans.columns.version',
      cell: (row) => faDigits(toNumber(row.version) ?? 0),
    },
    {
      id: 'status',
      headerKey: 'console:plans.columns.status',
      cell: (row) => (
        <StatusChip tone={versionTone(row.status)} labelKey={`console:plans.version_status.${row.status}`} />
      ),
    },
    {
      id: 'base',
      headerKey: 'console:plans.columns.base_price',
      cell: (row) => <Money amountMinor={row.basePriceMinor} currency={row.currency} unit="Rial" />,
    },
    {
      id: 'perProvider',
      headerKey: 'console:plans.columns.per_provider_price',
      cell: (row) => <Money amountMinor={row.perProviderPriceMinor} currency={row.currency} unit="Rial" />,
    },
    {
      id: 'features',
      headerKey: 'console:plans.columns.features',
      cell: (row) => faDigits(row.features.length),
    },
    {
      id: 'limits',
      headerKey: 'console:plans.columns.branches',
      cell: (row) => {
        const value = toNumber(row.limits['limit.branches']);
        return value === null ? t('console:tenant.usage.unlimited') : faDigits(value);
      },
    },
    {
      id: 'subscribers',
      headerKey: 'console:plans.columns.subscribers',
      cell: (row) => faDigits(toNumber(row.subscribers) ?? 0),
    },
    {
      id: 'publishedAt',
      headerKey: 'console:plans.columns.published_at',
      cell: (row) => (row.publishedAt ? <JalaliDate value={row.publishedAt} /> : t('common:states.none')),
    },
    {
      id: 'actions',
      headerKey: 'console:plans.columns.actions',
      cell: (row) =>
        row.status === 'Draft' ? (
          <Button
            size="small"
            variant="contained"
            onClick={() => onPublish(row)}
            data-testid={`plan-publish-${plan.code}-${toNumber(row.version) ?? 0}`}
          >
            {t('console:plans.publish')}
          </Button>
        ) : null,
    },
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-plan-code={plan.code}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="h3" component="h2" sx={{ flex: 1 }}>
          <bdi dir="ltr">{plan.code}</bdi>
        </Typography>
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          onClick={onNewVersion}
          data-testid={`plan-new-version-${plan.code}`}
        >
          {t('console:plans.new_version')}
        </Button>
      </Stack>
      <DataTable
        captionKey="console:plans.versions_caption"
        columns={columns}
        rows={versions}
        getRowId={(row) => row.id}
        emptyTitleKey="console:plans.no_versions"
      />
    </Paper>
  );
}

export function PlansPage() {
  const { t } = useTranslation('console');
  const { api } = usePlatformSession();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PlanView | null>(null);
  const [publishing, setPublishing] = useState<{ plan: PlanView; version: PlanVersionView } | null>(null);

  const plans = useQuery({
    queryKey: consoleKeys.plans,
    queryFn: () => unwrap(api.GET('/api/v1/platform/console/plans')),
  });

  const publish = useMutation({
    mutationFn: (versionId: string) =>
      unwrap(
        api.POST('/api/v1/platform/console/plan-versions/{id}:publish', {
          params: { path: { id: versionId } },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: consoleKeys.plans });
      setPublishing(null);
    },
  });

  return (
    <ListPage
      screenId={SCR_CONSOLE_004}
      titleKey="console:plans.title"
      subtitleKey="console:plans.subtitle"
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreating(true)}
          data-testid="plan-create"
        >
          {t('console:plans.create')}
        </Button>
      }
    >
      <QueryState
        isPending={plans.isPending}
        error={plans.error}
        data={plans.data}
        onRetry={() => void plans.refetch()}
      >
        {(data) => (
          <Stack spacing={2}>
            {[...data.items]
              .sort((a, b) => (toNumber(a.sortOrder) ?? 0) - (toNumber(b.sortOrder) ?? 0))
              .map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  onNewVersion={() => setEditing(plan)}
                  onPublish={(version) => setPublishing({ plan, version })}
                />
              ))}
          </Stack>
        )}
      </QueryState>
      <CreatePlanDialog open={creating} onClose={() => setCreating(false)} />
      {editing && <PlanVersionDialog plan={editing} open onClose={() => setEditing(null)} />}
      <ConfirmDialog
        open={publishing !== null}
        titleKey="console:plans.publish_title"
        messageKey="console:plans.publish_message"
        messageValues={{
          code: publishing?.plan.code ?? '',
          version: faDigits(toNumber(publishing?.version.version) ?? 0),
        }}
        confirmKey="console:plans.publish"
        busy={publish.isPending}
        error={publish.error}
        onCancel={() => {
          publish.reset();
          setPublishing(null);
        }}
        onConfirm={() => publishing && publish.mutate(publishing.version.id)}
      />
    </ListPage>
  );
}
